import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiHome,
  FiUser,
  FiUsers,
  FiImage,
  FiCreditCard,
  FiPlusCircle,
  FiCheckCircle,
  FiLogOut,
  FiChevronLeft,
  FiChevronRight,
  FiAward,
  FiChevronDown,
  FiLayout,
  FiBook,
  FiCheckSquare,
  FiFlag,
  FiUpload,
} from 'react-icons/fi';
import useAuthStore from '../../stores/useAuthStore';
import useApplicationStore from '../../stores/useApplicationStore';

const Sidebar = ({ isOpen, onToggle, isMobile }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuthStore();
  const { getDashboard } = useApplicationStore();

  const dashboard = getDashboard();

  // State to track expanded groups
  const [expandedGroups, setExpandedGroups] = useState(['dashboard']);

  // Menu items configuration with grouping
  const menuGroups = [
    {
      key: 'dashboard',
      title: null, // No title for dashboard
      icon: null,
      items: [
        {
          label: 'Dashboard',
          icon: FiHome,
          path: '/app/dashboard',
          isCompleted: null,
        },
      ],
    },
    {
      key: 'application-form',
      title: 'Application Form',
      icon: FiLayout, // Added icon
      items: [
        {
          label: 'New Application',
          icon: FiPlusCircle,
          path: '/app/start',
          isCompleted: null,
        },
        {
          label: 'Bio Data',
          icon: FiUser,
          path: '/app/bio',
          isCompleted: dashboard.bio,
        },
        {
          label: 'Parents/Guardians',
          icon: FiUsers,
          path: '/app/parents',
          isCompleted: dashboard.ref,
        },
      ],
    },
    {
      key: 'academic-records',
      title: 'Academic Records',
      icon: FiBook, // Added icon
      items: [
        {
          label: 'O-Level Results',
          icon: FiAward,
          path: '/app/olevel',
          isCompleted: dashboard.olevel && dashboard.result,
        },
        {
          label: 'A-Level Results',
          icon: FiAward,
          path: '/app/alevel',
          isCompleted: null,
        },
      ],
    },
    {
      key: 'finalization',
      title: 'Finalization',
      icon: FiCheckSquare, // Added icon
      items: [
        {
          label: 'Photo Upload',
          icon: FiImage,
          path: '/app/photo',
          isCompleted: dashboard.photo,
        },
        {
          label: 'Payment',
          icon: FiCreditCard,
          path: '/app/pay',
          isCompleted: dashboard.pay,
        },
        {
          label: 'Credential Upload',
          icon: FiUpload,
          path: '/app/credentials',
          isCompleted: null,
        },
      ],
    },
    {
      key: 'admission',
      title: 'Admission',
      icon: FiFlag, // Added icon
      items: [
        {
          label: 'Admission Status',
          icon: FiCheckCircle,
          path: '/app/admission',
          isCompleted: null,
        },
        {
          label: 'Acceptance',
          icon: FiAward,
          path: '/app/accept',
          isCompleted: null,
        },
      ],
    },
  ];

  // Auto-expand group when user navigates to a page in that group
  useEffect(() => {
    const currentGroup = menuGroups.find((group) =>
      group.items.some((item) => item.path === location.pathname)
    );
    if (currentGroup && currentGroup.key && !expandedGroups.includes(currentGroup.key)) {
      setExpandedGroups((prev) => [...prev, currentGroup.key]);
    }
  }, [location.pathname]);

  // Toggle group expansion
  const toggleGroup = (groupKey) => {
    setExpandedGroups((prev) =>
      prev.includes(groupKey)
        ? prev.filter((key) => key !== groupKey)
        : [...prev, groupKey]
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path;

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onToggle(); // Close sidebar on mobile after navigation
    }
  };

  // Sidebar variants for animation
  const sidebarVariants = {
    open: {
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
    closed: {
      x: isMobile ? '-100%' : 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30,
      },
    },
  };

  return (
    <>
      {/* Backdrop for mobile */}
      <AnimatePresence>
        {isMobile && isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onToggle}
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        variants={sidebarVariants}
        animate={isOpen || !isMobile ? 'open' : 'closed'}
        className={`fixed top-0 left-0 h-full bg-gradient-to-b from-primary-600 to-primary-700 shadow-xl z-50 flex flex-col ${isOpen ? 'w-72' : isMobile ? 'w-72' : 'w-20'
          } transition-all duration-300`}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between p-4 border-b border-white/10">
          {isOpen ? (
            <div className="flex items-center space-x-3">
              <img
                src="/images/college-logo.png"
                alt="COE Kangere"
                className="w-10 h-10 rounded-lg object-contain bg-white/20 backdrop-blur-sm p-0.5"
              />
              <div>
                <div className="text-white font-bold text-sm leading-none">COE Kangere</div>
                <div className="text-primary-100 text-xs leading-none mt-1">Admission Portal</div>
              </div>
            </div>
          ) : (
            !isMobile && (
              <img
                src="/images/college-logo.png"
                alt="COE Kangere"
                className="w-10 h-10 rounded-lg object-contain bg-white/20 backdrop-blur-sm p-0.5 mx-auto"
              />
            )
          )}

          {/* Collapse/Expand Toggle - Desktop only */}
          {!isMobile && (
            <button
              onClick={onToggle}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-all"
            >
              {isOpen ? (
                <FiChevronLeft className="w-5 h-5" />
              ) : (
                <FiChevronRight className="w-5 h-5" />
              )}
            </button>
          )}
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto py-4 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
          {menuGroups.map((group) => {
            const isGroupExpanded = expandedGroups.includes(group.key);
            const GroupIcon = group.icon; // Get group icon

            return (
              <div key={group.key} className="mb-2">
                {/* Group Title - Collapsible Header */}
                {group.title && isOpen && (
                  <button
                    onClick={() => toggleGroup(group.key)}
                    className="w-full px-4 py-2 flex items-center justify-between text-primary-200 hover:text-white transition-colors group"
                  >
                    <div className="flex items-center space-x-2">
                      {GroupIcon && <GroupIcon className="w-4 h-4" />} {/* Render Group Icon */}
                      <span className="text-xs font-semibold uppercase tracking-wider">
                        {group.title}
                      </span>
                    </div>
                    <motion.div
                      animate={{ rotate: isGroupExpanded ? 180 : 0 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FiChevronDown className="w-4 h-4" />
                    </motion.div>
                  </button>
                )}

                {/* Group Items with Animation */}
                <AnimatePresence initial={false}>
                  {(isGroupExpanded || !group.title || (!isOpen && !isMobile)) && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="space-y-1 px-2 py-1">
                        {group.items.map((item) => {
                          const Icon = item.icon;
                          const active = isActive(item.path);

                          return (
                            <button
                              key={item.path}
                              onClick={() => handleNavigation(item.path)}
                              className={`w-full flex items-center space-x-3 px-3 py-2.5 rounded-lg transition-all group ${active
                                ? 'bg-white text-primary-700 shadow-lg'
                                : 'text-white hover:bg-white/10'
                                } ${!isOpen && !isMobile ? 'justify-center' : ''}`}
                              title={!isOpen && !isMobile ? item.label : ''}
                            >
                              <div className="relative">
                                <Icon
                                  className={`w-5 h-5 ${active ? 'text-primary-700' : 'text-white group-hover:text-primary-100'
                                    }`}
                                />
                                {/* Completion Badge */}
                                {item.isCompleted && (
                                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-success-500 rounded-full border-2 border-white" />
                                )}
                              </div>

                              {(isOpen || isMobile) && (
                                <div className="flex-1 flex items-center justify-between">
                                  <span
                                    className={`text-sm font-medium ${active ? 'text-primary-700' : 'text-white'
                                      }`}
                                  >
                                    {item.label}
                                  </span>

                                  {/* Completion Indicator */}
                                  {item.isCompleted && (
                                    <FiCheckCircle className="w-4 h-4 text-success-500" />
                                  )}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-white/10">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center space-x-3 px-3 py-2.5 text-white hover:bg-danger-500/20 rounded-lg transition-all group ${!isOpen && !isMobile ? 'justify-center' : ''
              }`}
            title={!isOpen && !isMobile ? 'Logout' : ''}
          >
            <FiLogOut className="w-5 h-5 group-hover:text-danger-200" />
            {(isOpen || isMobile) && (
              <span className="text-sm font-medium">Sign Out</span>
            )}
          </button>
        </div>
      </motion.aside>

      {/* Spacer - Desktop only (to push content) */}
      {!isMobile && (
        <div
          className={`${isOpen ? 'w-72' : 'w-20'} transition-all duration-300 flex-shrink-0`}
        />
      )}
    </>
  );
};

export default Sidebar;
